import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import CustomTable from "../../../../../CustomHeadlessTable/CustomTable";
import { unitFromDropdownOrCache } from "../../../../../CustomHeadlessTable/lcaDropdownUnit";
import CustomInputComponent from "../../../../../CustomHeadlessTable/components/CustomInputComponent";
import {
  getAllLossesA2LCA,
  addLossesA2LCA,
  updateLossesA2LCA,
  deleteLossesA2LCA,
} from "../../../../../../apis/Product/TransportA2Api";
import { getProductLossesA2MaterialDropdown } from "../../../../../../apis/Dropdown";
import { IconButton } from "@mui/material";
import { KeyboardArrowLeft, KeyboardArrowRight } from "@mui/icons-material";
import { TableCompareTrigger } from "../../../components/lcaComparison";

// Impact categories configuration
const IMPACT_CATEGORIES = [
  { name: "Climate change", unit: "kg CO₂-eq", key: "global_warming" },
  { name: "Ozone depletion", unit: "kg CFC-11-eq", key: "ozone_depletion" },
  { name: "Photochemical ozone formation", unit: "kg NMVOC-eq", key: "smog" },
  { name: "Acidification", unit: "mol H⁺-eq", key: "acidification" },
  { name: "Eutrophication, terrestrial", unit: "mol N-eq", key: "eutrophication_terrestrial" },
  { name: "Eutrophication, freshwater", unit: "kg P-eq", key: "eutrophication_freshwater" },
  { name: "Eutrophication, marine", unit: "kg N-eq", key: "eutrophication_marine" },
  { name: "Human toxicity, cancer effects", unit: "CTUh", key: "carcinogenics" },
  { name: "Human toxicity, non-cancer effects", unit: "CTUh", key: "non_carcinogenics" },
  { name: "Particulate matter", unit: "disease incidence", key: "respiratory_effects" },
  { name: "Ecotoxicity, freshwater", unit: "CTUe", key: "ecotoxicity" },
  { name: "Resource use, energy carriers", unit: "MJ", key: "fossil_fuel_depletion" },
  { name: "Land use", unit: "Pt", key: "land_use" },
  { name: "Water use", unit: "m³ world-eq deprived", key: "water_use" },
  { name: "Resource use, mineral and metals", unit: "kg Sb-eq", key: "resource_use_minerals_metals" },
  { name: "Ionising radiation, human health", unit: "kBq U235-eq", key: "ionising_radiation" },
];

const LossesA2Table = ({ productId }) => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [currentImpactCategoryIndex, setCurrentImpactCategoryIndex] = useState(0);
  const [materialDropdown, setMaterialDropdown] = useState([]);
  const [unitMaterials, setUnitMaterials] = useState({});

  // Fetch material dropdown
  useEffect(() => {
    getProductLossesA2MaterialDropdown({
      setDropdown: setMaterialDropdown,
    });
  }, []);

  const toggleColumnVisibility = useCallback((columnId) => {
    setHiddenColumns((prev) =>
      prev.includes(columnId)
        ? prev.filter((id) => id !== columnId)
        : [...prev, columnId]
    );
  }, []);

  // Update units when data or dropdown changes
  useEffect(() => {
    if (data.length > 0 && materialDropdown.length > 0) {
      const newUnitMaterials = {};
      data.forEach((row) => {
        const materialCode =
          typeof row.material === "string"
            ? row.material
            : row.material?.value || row.material?.code;
        if (materialCode) {
          const selectedMaterial = materialDropdown.find(
            (option) =>
              !option.isCategory &&
              (option.code === materialCode ||
                option.value === materialCode)
          );
          if (selectedMaterial) {
            newUnitMaterials[row.id] = selectedMaterial.unit || "xx";
          }
        }
      });
      setUnitMaterials(newUnitMaterials);
    }
  }, [data, materialDropdown]);

  const currentImpactCategory = IMPACT_CATEGORIES[currentImpactCategoryIndex];

  const handlePreviousImpactCategory = useCallback(() => {
    setCurrentImpactCategoryIndex((prev) =>
      prev === 0 ? IMPACT_CATEGORIES.length - 1 : prev - 1
    );
  }, []);

  const handleNextImpactCategory = useCallback(() => {
    setCurrentImpactCategoryIndex((prev) =>
      prev === IMPACT_CATEGORIES.length - 1 ? 0 : prev + 1
    );
  }, []);

  const fetchData = useCallback(() => {
    if (productId) {
      getAllLossesA2LCA({
        productId,
        setObjects: setData,
      });
    }
  }, [productId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAdd = useCallback(
    (newRow) => {
      // Extract material code
      const material =
        typeof newRow.material === "string"
          ? newRow.material
          : newRow.material?.code || newRow.material?.value || null;

      addLossesA2LCA({
        productId,
        own_name: newRow.own_name || null,
        material: material,
        amount: newRow.amount || 0,
        callback: fetchData,
      });
    },
    [productId, fetchData]
  );

  const handleEdit = useCallback(
    (updatedRow) => {
      // Extract material code
      const material =
        typeof updatedRow.material === "string"
          ? updatedRow.material
          : updatedRow.material?.code || updatedRow.material?.value || null;

      updateLossesA2LCA({
        productId,
        lossId: updatedRow.id,
        own_name: updatedRow.own_name || null,
        material: material,
        amount: updatedRow.amount || 0,
        objects: data,
        setObjects: setData,
      });
    },
    [productId, data]
  );

  const handleDelete = useCallback(
    (rowId) => {
      deleteLossesA2LCA({
        productId,
        lossId: rowId,
        callback: fetchData,
      });
    },
    [productId, fetchData]
  );

  const columns = useMemo(
    () => [
      {
        id: "own_name",
        header: "Own name",
        accessor: "own_name",
        type: "text",
        placeholder: "user types...",
      },
      {
        id: "material",
        header: "Material lost *",
        accessor: "material",
        type: "dropdown",
        placeholder: "Select material...",
        isCategory: true,
        unit: (rowId, rowData) =>
          unitFromDropdownOrCache(
            rowData,
            "material",
            materialDropdown,
            unitMaterials,
            rowId
          ),
      },
      {
        id: "amount",
        header: "Amount",
        accessor: "amount",
        type: "number",
        placeholder: "User types...",
        unit: (rowId, rowData) =>
          unitFromDropdownOrCache(
            rowData,
            "material",
            materialDropdown,
            unitMaterials,
            rowId
          ),
      },
      {
        id: currentImpactCategory.key,
        header: (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <IconButton
              size="small"
              onClick={handlePreviousImpactCategory}
              style={{ padding: "4px" }}
            >
              <KeyboardArrowLeft fontSize="small" />
            </IconButton>
            <span>
              {currentImpactCategory.name} [{currentImpactCategory.unit}]
            </span>
            <IconButton
              size="small"
              onClick={handleNextImpactCategory}
              style={{ padding: "4px" }}
            >
              <KeyboardArrowRight fontSize="small" />
            </IconButton>
            <TableCompareTrigger />
          </div>
        ),
        accessor: currentImpactCategory.key,
        type: "custom",
        optional: true,
        canSort: false,
        cell: ({ row }) => {
          const currentCategory = IMPACT_CATEGORIES[currentImpactCategoryIndex];
          // Backend returns impact categories directly on the object (e.g., global_warming)
          const impactValue =
            row.original[currentCategory.key] ||
            row.original[`impact_${currentCategory.key}`] ||
            row.original.impact_categories?.[currentCategory.key] ||
            0;
          return <CustomInputComponent value={impactValue} readOnly />;
        },
        subtotalUnit: currentImpactCategory.unit,
        subtotalFixed: true,
        width: "200px",
      },
    ],
    [
      currentImpactCategory,
      handlePreviousImpactCategory,
      handleNextImpactCategory,
      unitMaterials,
      materialDropdown,
      data,
      currentImpactCategoryIndex,
    ]
  );

  const impactCategoryAccessor = currentImpactCategory.key;

  const disableRules = useMemo(
    () => ({
      [impactCategoryAccessor]: () => true, // Make impact category read-only
    }),
    [impactCategoryAccessor]
  );

  const subtotalColumns = useMemo(
    () => ({
      [impactCategoryAccessor]: {
        label: `Total ${currentImpactCategory.name}`,
      },
    }),
    [impactCategoryAccessor, currentImpactCategory.name]
  );

  const memoizedDropdownData = useMemo(
    () => ({
      material: materialDropdown || [],
    }),
    [materialDropdown]
  );

  return (
    <div>
      <CustomTable
        data={data}
        setData={setData}
        columns={columns || []}
        onAdd={handleAdd}
        onEdit={handleEdit}
        onDelete={handleDelete}
        hiddenColumns={hiddenColumns}
        toggleColumnVisibility={toggleColumnVisibility}
        dropdownData={memoizedDropdownData}
        disableRules={disableRules}
        subtotalColumns={subtotalColumns}
        customInputs={useMemo(
          () => ({ [impactCategoryAccessor]: CustomInputComponent }),
          [impactCategoryAccessor]
        )}
        comparisonConfig={{ categories: IMPACT_CATEGORIES }}
      />
    </div>
  );
};

export default LossesA2Table;

